import { useEffect, useState } from "react";
import Layout from "../../Components/Layout/Layout";
import { Alert, Box, CircularProgress, Grid } from "@mui/material";
import { useParams } from "react-router";
import {
	Appointment,
	AssessmentOrder,
	BookingCode, CatalogVehicle,
	Equipment,
	LeasmanVehicle,
	ReturnForm,
	ReturnOrder,
	Sale,
	Validation,
	Vehicle,
	VehicleAssessment,
	VehicleAssessmentDamage
} from "../../system/Domain";
import useVehicle from "../../system/useVehicle";
import formatNumber from "../../system/formatNumber";
import useAxiosEffect from "../../system/useAxiosEffect";
import axios from "axios";
import RemarketingSidebar from "./RemarketingSidebar";
import useEvent from "../../system/useEvent";
import useChannel from "../../system/useChannel";
import RemarketingForm from "./RemarketingForm";
import EquipmentHighlights from "./EquipmentHighlights";
import VehicleNavigation from "Navigation/VehicleNavigation";
import VehicleHeader from "./VehicleHeader";
import { PictureReference } from "../../Components/PicturesTile";
import AssessmentDamages, { lasmanContractServiceIsEnsurance } from "./Validation/AssessmentDamages";
import { isServiceValid } from "./Validation/ServiceChips";
import useBookingCodes from "../../system/useBookingCodes";
import ValidationInformation from "./Validation/ValidationInformation";
import FinalInvoiceTile from "./Validation/FinalInvoiceTile";
import { containsAny } from "../../utils";
import { setUserMessage } from "../../system/reducers/userMessage/actions";
import { useDispatch } from "react-redux";
import moment from "moment/moment";
import ValidationStatusHistoryTile from "./Validation/ValidationStatusHistoryTile";
import EquipmentList from "./EquipmentList";
import _ from "lodash";
import ValidationStopsTile from "./Validation/ValidationStopsTile";
import ValidationDocuments from "./Validation/ValidationDocuments";
import SaleDealerInformationTile from "../VehicleSales/SaleDealerInformationTile";
import VehicleAccidentsTile from "./VehicleAccidentsTile";

export type RemarketingActions = (typeof Actions)[number];
const Actions = ["Endabrechnung", "Information", "Verkaufsvorbereitung"] as const;

export interface FinalInvoiceMetadata {
	returnForms?: FinalInvoiceReturnFormMetaData[]
	initialCollectionAppointment?: Appointment
}

// Relative deviation between return form and assessment mileage above which a warning is shown
const MILEAGE_DEVIATION_THRESHOLD = 0.1;

// smart #1 registered before this date and every smart #3 were delivered with a single key only
const SMART_TWO_KEYS_FROM = new Date("2024-11-01");

export interface FinalInvoiceReturnFormMetaData {
	mileage?: number
	id: string
	isFirst: boolean
	dateCompleted: string
}

const mapBookingKeys = (damage: VehicleAssessmentDamage, hasTireService: boolean, hasSomeGlassCoveringInsurance: boolean) => {
	if (damage.type === "Mercantile") return "WM01";
	if (hasTireService && damage.type === "Tires") return "RS03";
	// Must precede the generic MissingParts rule, otherwise missing keys end up as "Fehlteil Belastung"
	const isWheelOrToolKey = containsAny(damage.text, ['Radschlüssel', 'Radmutter', 'Felgen', 'Schraubenschlüssel', 'Standheizung']);
	if (!isWheelOrToolKey && containsAny(damage.text, ['Schlüssel', 'Fernbedienung', 'Key', 'Remote'])) return "RU02-3";
	if (damage.type === "MissingParts") return "RU02-2";
	const glass = containsAny(damage.text, ['Windschutzscheibe', 'Frontscheibe', 'WSS', 'Glasschaden', 'Glas']);
	if (glass && hasSomeGlassCoveringInsurance) return "RS01";
	if (glass && !hasSomeGlassCoveringInsurance) return "RS07";
	if (containsAny(damage.text, ['Entklebung', 'Sticker', 'Entschriftung', 'Beklebung', 'Beklebt', 'Foli', 'Werb'])) return "RU15";
	if (containsAny(damage.text, ['Garantie'])) return "RU14";
	return "RU02-1";
};

const wheelKeywords = ["Felge", "Radkappe", "Radzierblende", "Radabdeckung", "Raddeckel", "Stahlfelge", "Alufelge"];
const smartRepairKeywords = ["Smart Repair", "Smartrepair"];

const isDekraAssessment = (assessment: VehicleAssessment) =>
	assessment.source === "Dfo" || assessment.source === "Lea";

const isDefaultPartialDepreciation = (damage: VehicleAssessmentDamage, assessment: VehicleAssessment): boolean => {
	if (!isDekraAssessment(assessment)) return false;

	const text = damage.text?.trim() ?? "";

	// Case 1: exact match for "Lackvorbereitung"
	if (text.toLowerCase() === "lackvorbereitung") return true;

	// Case 2: contains a wheel keyword but no smart repair keyword
	if (containsAny(text, wheelKeywords) && !containsAny(text, smartRepairKeywords)) return true;

	return false;
};

const extendDamages = (vehicle: Vehicle, assessment: VehicleAssessment, bookingCodes: BookingCode[], discountValue: number | null, calculationType: ValidationDeductionCalculationType) => {
	const hasTireService = vehicle.remarketing.leasmanContract?.services?.some(v => v.type?.id == "RF" && v.isOpen === false);
	const hasSomeGlassCoveringInsurance = vehicle.remarketing.leasmanContract?.services?.some(v => lasmanContractServiceIsEnsurance(v));

	const discountFactor = (discountValue ?? 100) / 100;

	const mapBookingCodes = (damage: VehicleAssessmentDamage, hasTireService: boolean, hasSomeGlassCoveringInsurance: boolean): BookingCode => {
		const codeResult = mapBookingKeys(damage, hasTireService, hasSomeGlassCoveringInsurance);

		return bookingCodes.find(f => f.key === codeResult);
	};

	return assessment.damages.map(m => {
		const partialDepreciation = m.partialDepreciation === true || isDefaultPartialDepreciation(m, assessment);
		return {
			...m,
			bookingCode: mapBookingCodes(m, hasTireService, hasSomeGlassCoveringInsurance),
			partialDepreciation,
			factor: partialDepreciation ? discountFactor : 1,
			isNew: false,
			useAssessmentNetValues: calculationType === "Precalculated" && !!m.depreciationAmountNet
		};
	});
};

const calculateDeductionByLpdeTable = (dateFirstRegisteredBase: string, dateContractEndBase: string, mileage: number): number => {
	const dateFirstRegistered = moment(dateFirstRegisteredBase);
	const dateContractEnd = moment(dateContractEndBase);

	const months =
		(dateContractEnd.year() - dateFirstRegistered.year()) * 12 +
		(dateContractEnd.month() - dateFirstRegistered.month());

	// Up to 24 months
	if (months <= 24) {
		if (mileage <= 50_000) return 90;
		if (mileage <= 75_000) return 85;
		if (mileage <= 100_000) return 80;
		if (mileage <= 125_000) return 75;
		if (mileage <= 150_000) return 70;
		return 65;
	}

	// Up to 36 months
	if (months <= 36) {
		if (mileage <= 50_000) return 85;
		if (mileage <= 75_000) return 80;
		if (mileage <= 100_000) return 75;
		if (mileage <= 125_000) return 70;
		if (mileage <= 150_000) return 65;
		return 60;
	}

	// Up to 48 months
	if (months <= 48) {
		if (mileage <= 50_000) return 75;
		if (mileage <= 75_000) return 70;
		if (mileage <= 100_000) return 65;
		if (mileage <= 125_000) return 60;
		if (mileage <= 150_000) return 55;
		return 50;
	}

	// Up to 60 months
	if (months <= 60) {
		if (mileage <= 50_000) return 65;
		if (mileage <= 75_000) return 60;
		if (mileage <= 100_000) return 55;
		if (mileage <= 125_000) return 50;
		if (mileage <= 150_000) return 45;
		return 40;
	}

	// Over 60 months
	if (mileage <= 50_000) return 55;
	if (mileage <= 75_000) return 50;
	if (mileage <= 100_000) return 45;
	if (mileage <= 125_000) return 40;
	if (mileage <= 150_000) return 35;
	return 30;
};

const calculateDeductionByAyvensTable = (dateFirstRegisteredBase: string, dateContractEndBase: string, mileage: number): number => {
	const dateFirstRegistered = moment(dateFirstRegisteredBase);
	const dateContractEnd = moment(dateContractEndBase);

	const months =
		(dateContractEnd.year() - dateFirstRegistered.year()) * 12 +
		(dateContractEnd.month() - dateFirstRegistered.month());

	// Up to 12 months
	if (months <= 12) {
		if (mileage <= 10_000) return 100;
		if (mileage <= 30_000) return 90;
		if (mileage <= 120_000) return 80;
		if (mileage <= 150_000) return 70;
		return 70;
	}

	// Up to 24 months
	if (months <= 24) {
		if (mileage <= 50_000) return 80;
		if (mileage <= 120_000) return 70;
		if (mileage <= 150_000) return 60;
		return 60;
	}

	// Up to 36 months
	if (months <= 36) {
		if (mileage <= 50_000) return 75;
		if (mileage <= 120_000) return 65;
		if (mileage <= 150_000) return 55;
		return 45;
	}

	// Up to 48 months
	if (months <= 48) {
		if (mileage <= 50_000) return 70;
		if (mileage <= 120_000) return 60;
		if (mileage <= 150_000) return 50;
		return 40;
	}

	// Up to 72 months
	if (months <= 72) {
		if (mileage <= 50_000) return 65;
		if (mileage <= 120_000) return 55;
		if (mileage <= 150_000) return 45;
		return 35;
	}

	// Over 72 months
	if (mileage <= 50_000) return 30;
	if (mileage <= 150_000) return 20;
	return 20;
};

// vehicle.type.costs.listPrice.netAmount // assessment.market2Price
export const calculateDeductionsByFormula = (listPriceNetAmount: number, market2Price: number): number => {
	const listPriceNotAvailable = listPriceNetAmount <= 0;
	return listPriceNotAvailable ? 100
		: Math.min(Math.max(Math.ceil(market2Price * 100 / (listPriceNetAmount * 0.85)), 40), 100);
};

const getCalculationType = (vehicle?: Vehicle): ValidationDeductionCalculationType => {
	switch (vehicle?.remarketing?.leasmanContract?.depreciationCalculationType) {
		// case "MWTA": return "AyvensTable";
		// case "MIWM": return "AyvensTable";
		// case "FASS": return "AyvensTable";
		case "MWTI": return "LpdeTable";
		case "AWEF": return "Precalculated";
		default: return "AyvensTable"; // default and MWTA, MIWM, FASS
	}
};

export type ValidationDeductionCalculationType = "Precalculated" | "AyvensTable" | "DeductionFormula" | "LpdeTable"; // DeductionFormula is no longer in use

const RemarketingLayout = () => {
	const dispatch = useDispatch();
	const { id } = useParams<{ id: string }>();
	const [vehicle, setVehicle, reloadVehicle] = useVehicle(id);
	const [assessment, setAssessment] = useState<VehicleAssessment>();
	const [assessmentOrder, setAssessmentOrder] = useState<AssessmentOrder>()
	const [sale, setSale] = useState<Sale>();
	const [bookingCodes] = useBookingCodes();
	const [isLoading, setIsLoading] = useState(false);

	const [leasmanVehicles, setLeasmanVehicles] = useState<LeasmanVehicle[]>();
	const [leasmanVehicle, setLeasmanVehicle] = useState<LeasmanVehicle>();
	const [returnForm, setReturnForm] = useState<ReturnForm>();
	const [returnOrder, setReturnOrder] = useState<ReturnOrder>();
	const [metaData, setMetaData] = useState<FinalInvoiceMetadata>();
	const [equipment, setEquipment] = useState<Equipment[]>([]);
	const [catalogVehicle, setCatalogVehicle] = useState<CatalogVehicle>();
	const aboveDateThreshold = moment(assessment?.dateCreated).isAfter('2026-03-01');
	const calculationType: ValidationDeductionCalculationType = !aboveDateThreshold && assessment?.source === "Lea" ? "Precalculated": getCalculationType(vehicle);
	const [validation, setValidation] = useState<Validation>();

	const [discountValue, setDiscountValue] = useState<number | null>(null);

	const updateFactor = (val: number) => {
		setValidation((prev) => {
			if (!prev) return prev;

			return {
				...prev,
				damages: prev.damages?.map((damage) =>
					damage.partialDepreciation
						? { ...damage, factor: val }
						: damage
				)
			};
		});
	};

	useEffect(() => {
		let val: number | null = null;

		if (!validation || !vehicle || !assessment) return;

		switch (calculationType) {
			case "AyvensTable":
				if (!vehicle || !validation?.mileage || !validation?.contractEndDate) setDiscountValue(null);
				else {
					val = calculateDeductionByAyvensTable(vehicle.dateFirstRegistered, validation.contractEndDate, validation.mileage);
					setDiscountValue(val);
				}
				break;
			case "Precalculated":
			case "DeductionFormula":
				if (!vehicle || !assessment) setDiscountValue(null);
				else {
					val = calculateDeductionsByFormula(vehicle.type.costs.listPrice.netAmount, assessment.market2Price);
					setDiscountValue(val);
				}
				break;
			case "LpdeTable":
				if (!vehicle || !validation?.mileage || !validation?.contractEndDate) setDiscountValue(null);
				else {
					val = calculateDeductionByLpdeTable(vehicle.dateFirstRegistered, validation.contractEndDate, validation.mileage);
					setDiscountValue(val);
				}
				break;
		}

		if (val && validation.status !== "Completed" && validation.status !== "Transferred")
			validation.depreciationFactor = val / 100;

		updateFactor(val / 100);
	}, [validation?.mileage, validation?.contractEndDate, vehicle, assessment]);

	// When contractEndDate changes, correct insurance-dependent booking codes so the
	// user never ends up with a booking code that is hidden in the dropdown.
	// RS01 (glass with insurance) ↔ RS07 (glass without insurance) are exact semantic
	// inverses and can be swapped safely. Other service-gated codes (RS02, RS03, RS01-1)
	// are never auto-assigned and must be fixed manually by the user.
	useEffect(() => {
		if (!validation?.contractEndDate || !vehicle || !bookingCodes) return;

		const contractEndDate = validation.contractEndDate;
		const services = vehicle.remarketing.leasmanContract?.services;
		const hasInsurance = services?.some(v => lasmanContractServiceIsEnsurance(v) && isServiceValid(v, contractEndDate));

		setValidation(prev => {
			if (!prev?.damages) return prev;

			let changed = false;
			const correctedDamages = prev.damages.map(damage => {
				const key = damage.bookingCode?.key;
				if (key === "RS01" && !hasInsurance) {
					changed = true;
					return { ...damage, bookingCode: bookingCodes.find(bc => bc.key === "RS07")! };
				}
				if (key === "RS07" && hasInsurance) {
					changed = true;
					return { ...damage, bookingCode: bookingCodes.find(bc => bc.key === "RS01")! };
				}
				return damage;
			});

			return changed ? { ...prev, damages: correctedDamages } : prev;
		});
	}, [validation?.contractEndDate, vehicle, bookingCodes]);

	const [rnpPictures, setRnpPictures] = useState<PictureReference[]>([]);

	const [mode, setMode] = useState<RemarketingActions>("Information");

	const wasReset = (() => {
		if (!vehicle?.remarketing?.validation) return false;
		const history = vehicle.remarketing.validation.history;
		const lastHistoryEntry = history.length > 0 ? history[history.length - 1] : undefined;

		return lastHistoryEntry?.action === "Reset";
	})();

	useEffect(() => {
		if (!vehicle || !assessment || !bookingCodes) return;

		if (vehicle.remarketing.validation) {
			// all cases where there was no save action have not yet any damages calculated
			const seedInitialDamages = !vehicle.remarketing.validation.history.some(s => s.action == "InitialSave") && !vehicle.remarketing.validation.damages;

			if (seedInitialDamages || wasReset) {
				vehicle.remarketing.validation.damages = extendDamages(vehicle, assessment, bookingCodes, discountValue, calculationType);
			}

			setValidation(vehicle.remarketing.validation);

			return;
		}

		setValidation({
			mileage: null,
			contractEndDate: null,
			damages: extendDamages(vehicle, assessment, bookingCodes, discountValue, calculationType),
			status: "Started",
			isStopped: false
		});
	}, [vehicle, assessment, bookingCodes]);

	useEffect(() => {
		if (!vehicle && !leasmanVehicle) {
			setEquipment([]);
			return;
		}

		let equipment = vehicle?.equipment || [];
		if (!!leasmanVehicle) {
			equipment = [
				...equipment,
				...leasmanVehicle.equipment.filter((e) => !equipment.some((e2) => e2.id === e.id))
			];
		}

		setEquipment(_.orderBy(equipment, (e) => e.text?.toLowerCase()));
	}, [vehicle, leasmanVehicle]);

	const [, reloadCatalog] = useAxiosEffect(
		async (config) => {
			if (!vehicle?.type.id) {
				return;
			}

			try {
				const { data } = await axios.get<CatalogVehicle>(`/api/catalog/vehicles/${vehicle.type.id}`, config);
				setCatalogVehicle(data);
			} catch (error) {
				if (axios.isAxiosError(error) && error.response?.status === 404) {
					return; // No Schwacke catalog entry — render page without type details
				}
				throw error;
			}
		},
		[vehicle?.type?.id]
	);

	const [, reloadRnpPictures] = useAxiosEffect(
		async (config) => {
			if (!vehicle?.vin) return;
			if (!vehicle.remarketing?.arrival?.orderId) return;

			const { data } = await axios.get<PictureReference[]>(`/api/orders/transports/${vehicle.remarketing.arrival.orderId}/return-forms/images/all`, config);

			setRnpPictures(data);
		}, [vehicle?.vin]
	);

	const [, reloadReturnOrder] = useAxiosEffect(
		async (config) => {
			if (!vehicle?.vin || !vehicle.remarketing?.arrival?.orderId) return;

			const { data } = await axios.get<ReturnOrder>(`/api/orders/returns`, {
				...config,
				params: {
					orderId: vehicle.remarketing?.arrival?.orderId,
					perspective: "Processed"
				}
			});

			setReturnOrder(data[0]);
		}, [vehicle?.vin]
	);

	const [, reloadMetaData] = useAxiosEffect(
		async (config) => {
			if (!vehicle?.vin || !vehicle.remarketing?.arrival?.orderId) return;

			const { data } = await axios.get<FinalInvoiceMetadata>(`/api/vehicles/${vehicle.id}/final-invoice/meta-data`, { ...config });

			setMetaData(data);
		}, [vehicle?.vin]
	);

	const [, reloadLeasman] = useAxiosEffect(
		async (config) => {
			if (!vehicle) {
				setLeasmanVehicles(undefined);
				return;
			}

			const { data } = await axios.get<LeasmanVehicle[]>(`/api/leasman/vehicles`, {
				...config,
				params: {
					vin: vehicle.vin
				}
			});

			setLeasmanVehicles(data);
		},
		[vehicle?.vin]
	);

	useAxiosEffect(
		async (config) => {
			if (!vehicle?.remarketing?.assessment?.assessmentId) {
				setAssessment(undefined);
				return;
			}

			const { data } = await axios.get<VehicleAssessment>(
				`/api/assessments/${vehicle.remarketing.assessment.assessmentId}`,
				config
			);

			setAssessment(data);
		},
		[vehicle?.remarketing?.assessment?.assessmentId]
	);

	useAxiosEffect(
		async (config) => {
			if (!vehicle?.remarketing?.assessment?.orderId) {
				setAssessmentOrder(undefined);
				return;
			}

			const { data } = await axios.get<AssessmentOrder>(
				`/api/orders/assessments/${vehicle.remarketing.assessment.orderId}`,
				config
			);

			setAssessmentOrder(data);
		},
		[vehicle?.remarketing?.assessment?.orderId]
	);

	useAxiosEffect(
		async (config) => {
			if (!vehicle?.remarketing?.saleId) {
				setSale(undefined);
				return;
			}

			const { data } = await axios.get<Sale>(`/api/sales/${vehicle.remarketing.saleId}`, config);
			setSale(data);
		},
		[vehicle?.remarketing?.saleId]
	);

	useEffect(() => {
		if (!vehicle || !vehicle.leasmanContractId || !leasmanVehicles || leasmanVehicles.length === 0) {
			setLeasmanVehicle(undefined);
			return;
		}

		const leasmanVehicle = leasmanVehicles.find((v) => v.contract.id === vehicle.leasmanContractId);
		setLeasmanVehicle(leasmanVehicle);
	}, [vehicle, leasmanVehicles]);

	useChannel("remarketing");

	const reload = () => Promise.all([reloadVehicle(), reloadLeasman()]);

	useEvent(
		"remarketing:updated",
		async (data) => {
			const { id } = data;

			if (!vehicle || !id) {
				return;
			}

			if (vehicle.id === id) {
				await reload();
			}
		},
		[vehicle?.id]
	);

	useEvent(
		"remarketing:prepared",
		async (data) => {
			const { ids } = data;

			if (!vehicle || !ids || !ids.length) {
				return;
			}

			if (ids.includes(vehicle.id)) {
				await reload();
			}
		},
		[vehicle?.id]
	);

	const disabledSale = !!vehicle?.remarketing.saleId && !sale?.corrections.some((c) => c.status === "Created");

	// LeasmanReference
	const referenceCreated = vehicle?.remarketing?.leasmanContract?.dateReferenceCreated;
	const tooLong = !referenceCreated || moment().diff(moment(referenceCreated), 'days') > 3;

	const disableValidation = (!!vehicle?.remarketing.saleId && !wasReset) || tooLong || !!vehicle?.remarketing.validation?.dateTransferred || vehicle?.remarketing.validation?.status === "Completed";

	const saveFinalInvoice = async () => {
		const { data } = await axios.post<Vehicle>(`/api/vehicles/${vehicle.id}/remarketing/validation`, validation);

		setVehicle(data);

		dispatch(setUserMessage(`Gespeichert`, "success"));
	};

	const rnpMileage = metaData?.returnForms?.find(f => f.isFirst)?.mileage;
	const assessmentMileage = assessment?.mileage;
	const mileageDeviation = rnpMileage && assessmentMileage
		? Math.abs(assessmentMileage - rnpMileage) / rnpMileage
		: null;
	const hasHighMileageDeviation = mileageDeviation !== null && mileageDeviation > MILEAGE_DEVIATION_THRESHOLD;

	const vehicleMake = vehicle?.type?.make?.name?.trim().toLowerCase();
	const vehicleModel = vehicle?.type?.model?.name ?? "";
	const firstRegistered = vehicle?.dateFirstRegistered ? new Date(vehicle.dateFirstRegistered) : null;
	const isSmartSingleKey = vehicleMake === "smart" && (
		/#\s*3/.test(vehicleModel)
		|| (/#\s*1/.test(vehicleModel) && firstRegistered !== null && firstRegistered < SMART_TWO_KEYS_FROM)
	);
	const hasMissingKeyDamage = !!assessment?.damages?.some(d =>
		d.type === "MissingParts" && containsAny(d.text, ['Schlüssel', 'Fernbedienung', 'Key', 'Remote']));

	const canTransferValidation = !!vehicle?.remarketing?.validation
		&& !vehicle.remarketing.validation.dateTransferred
		&& vehicle?.remarketing.validation?.status !== "Completed"
		&& vehicle.remarketing.validation?.isStopped !== true
	;

	return (
		<Layout
			title="Validierung"
			plateNumber={vehicle?.plateNumber}
			navigation={<VehicleNavigation vehicle={vehicle} />}
			sidebar={
				vehicle && (
					<RemarketingSidebar
						canTransferValidation={canTransferValidation}
						vehicle={vehicle}
						setVehicle={setVehicle}
						sale={sale}
						setSale={setSale}
						completedActions={[]}
						mode={mode}
						setMode={setMode}
						canAccessFinalInvoice={vehicle && assessment && true}
						saveFinalInvoice={saveFinalInvoice}
						isLoading={isLoading}
						setIsLoading={setIsLoading}
						disableValidation={disableValidation}
						tooLong={tooLong}
					/>
				)
			}
		>
			{(!vehicle || isLoading) && <CircularProgress size={48} />}
			{vehicle && !isLoading && (
				<>
					<VehicleHeader vehicle={vehicle} />
					{hasHighMileageDeviation && (
						<Alert severity="warning" sx={{ mb: 4 }}>
							<strong>Hohe Kilometerabweichung:</strong> Rücknahmeprotokoll {formatNumber(rnpMileage)} km, Gutachten {formatNumber(assessmentMileage)} km
							(Abweichung {formatNumber(mileageDeviation * 100)} %). Bitte den Kilometerstand manuell prüfen.
						</Alert>
					)}
					<Grid container spacing={6}>
						{mode === "Information" && (
							<ValidationInformation
								leasmanVehicle={leasmanVehicle}
								vehicle={vehicle}
								setVehicle={setVehicle}
								assessment={assessment}
								assessmentOrder={assessmentOrder}
								rnpPictures={rnpPictures}
								setReturnForm={setReturnForm}
							/>
						)}

						<Grid item xs={12} md={12}>
							{mode === "Endabrechnung" && (
								<Grid container spacing={3}>
									{assessment && bookingCodes && validation && (
										<Grid item xs={12} md={12}>
											<AssessmentDamages
												assessment={assessment}
												vehicle={vehicle}
												bookingCodes={bookingCodes}
												validation={validation}
												setValidation={setValidation}
												disabled={disableValidation}
												discountValue={discountValue}
												calculationType={calculationType}
											/>
										</Grid>
									)}
									<Grid item xs={12} md={6}>
										{validation && (
											<FinalInvoiceTile
												returnOrder={returnOrder}
												metaData={metaData}
												vehicle={vehicle}
												vehicleAssessment={assessment}
												validation={validation}
												setValidation={setValidation}
												disabled={disableValidation}
											/>
										)}
									</Grid>
									<Grid item xs={12} md={6}>
										{validation.history && (
											<ValidationStatusHistoryTile vehicle={vehicle} />
										)}
										{validation.validationDocumentUpload && (
											<ValidationDocuments validation={validation} assignedTo={vehicle.remarketing.assessment.assignedTo} />
										)}
										<ValidationStopsTile
											vehicle={vehicle}
											setVehicle={setVehicle}
										/>
									</Grid>
								</Grid>
							)}

							{mode === "Verkaufsvorbereitung" && (
								<Grid container spacing={3}>
									{isSmartSingleKey && (
										<Grid item xs={12}>
											<Alert severity="warning">
												<strong>smart: serienmäßig nur 1 Schlüssel.</strong> Für dieses Fahrzeug ({vehicleModel}) darf kein Fehlteil für einen zweiten Schlüssel erfasst werden.
												{hasMissingKeyDamage && " Das Gutachten enthält ein Fehlteil mit Schlüssel – bitte prüfen."}
											</Alert>
										</Grid>
									)}
									<Grid item xs={12} md={6}>
										<RemarketingForm
											disabled={disabledSale}
											vehicle={vehicle}
											setVehicle={setVehicle}
											assessment={assessment}
											returnForm={returnForm}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<SaleDealerInformationTile
											vehicle={vehicle}
											setVehicle={setVehicle}
											disabled={disabledSale}
										/>
										<Box mt={2}>&nbsp;</Box>
										<VehicleAccidentsTile accidents={vehicle.accidents} />
									</Grid>
									<Grid item xs={12}>
										<EquipmentHighlights
											disabled={disabledSale}
											vehicle={vehicle}
											setVehicle={setVehicle}
										/>
									</Grid>
									<Grid item xs={12}>
										<EquipmentList
											title="Sonderausstattung"
											equipments={equipment.filter((e) => e.isExtra)}
											vehicle={vehicle}
											setVehicle={setVehicle}
											catalogVehicle={catalogVehicle}
											leasmanVehicle={leasmanVehicle}
											disabled={!!vehicle?.remarketing.saleId}
										/>
									</Grid>
									<Grid item xs={12}>
										<EquipmentList
											title="Serienausstattung"
											equipments={equipment.filter((e) => !e.isExtra)}
											vehicle={vehicle}
											setVehicle={setVehicle}
											catalogVehicle={catalogVehicle}
											leasmanVehicle={leasmanVehicle}
											disabled={!!vehicle?.remarketing.saleId}
										/>
									</Grid>
								</Grid>
							)}
						</Grid>
					</Grid>
				</>
			)}
		</Layout>
	);
};

export default RemarketingLayout;
